import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Paperclip, Plus } from "lucide-react";
import ListView from "../pages/ListView";

const ensureDesign = () => {
  if (typeof document === "undefined") return;
  if (document.getElementById("relatedlists-design")) return;

  const tag = document.createElement("style");
  tag.id = "relatedlists-design";
  tag.innerHTML = `
  :root{
    --rl-bg:#ffffff;
    --rl-ink:#0f1a1c;
    --rl-ink-2:#1e2e30;
    --rl-muted:#5c6d6f;
    --rl-line:#dfe7e5;
    --rl-card:#ffffff;
    --rl-brand:#0d7d6f;
    --rl-radius:12px;
    --rl-shadow-sm:0 1px 3px rgba(9,20,19,.07);
    --rl-shadow-md:0 4px 12px rgba(9,20,19,.08);
  }
  .rl-tabs { display:flex; gap:8px; overflow:auto; padding:2px; }
  .rl-tab {
    border:1px solid var(--rl-line);
    background:#fff;
    color:var(--rl-ink);
    padding:8px 12px;
    border-radius:999px;
    font-weight:700;
    font-size:13px;
    cursor:pointer;
    transition:all .15s ease;
    box-shadow:var(--rl-shadow-sm);
    white-space:nowrap;
  }
  .rl-tab:hover { transform: translateY(-1px); box-shadow: var(--rl-shadow-md); }
  .rl-tab.active { background:var(--rl-brand); color:#fff; border-color:var(--rl-brand); }
  .rl-wrap { background: var(--rl-bg); border: 1px solid var(--rl-line); border-radius: var(--rl-radius); box-shadow: var(--rl-shadow-sm); }
  .rl-header {
    display:flex; align-items:center; justify-content:space-between;
    gap:10px; padding:10px 12px; border-bottom:1px solid var(--rl-line); background:#fff;
  }
  .rl-actions { display:flex; gap:8px; align-items:center; }
  .rl-btn {
    display:inline-flex; align-items:center; gap:8px;
    border:1px solid var(--rl-line);
    background:var(--rl-card);
    color:var(--rl-ink);
    padding:8px 12px;
    border-radius:999px;
    font-weight:700; font-size:13px;
    text-decoration:none;
    box-shadow:var(--rl-shadow-sm);
    transition:transform .15s ease, box-shadow .15s ease, border-color .15s ease;
    white-space:nowrap;
  }
  .rl-btn:hover { transform: translateY(-1px); box-shadow: var(--rl-shadow-md); border-color:var(--rl-brand); color:var(--rl-brand); }
  .rl-btn .plus { display:inline-flex; color:var(--rl-brand); }
  .rl-title { margin:0; font-size:14px; font-weight:800; color:var(--rl-ink-2); letter-spacing:.2px; }
  .rl-sub { color:var(--rl-muted); font-size:12px; }
  `;
  document.head.appendChild(tag);
};

ensureDesign();

const authHeaders = (token) => ({ Authorization: `Bearer ${token}` });

const RelatedLists = ({ tableName, recordId }) => {
  const [relatedTables, setRelatedTables] = useState([]);
  const [selectedTab, setSelectedTab] = useState(null);
  const [displayValue, setDisplayValue] = useState("");
  const [parentTableLabel, setParentTableLabel] = useState("");

  const token = localStorage.getItem("token");
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    if (!tableName || !recordId || recordId === "new" || !token) return;

    let isMounted = true;

    const loadRelatedBootstrap = async () => {
      try {
        const res = await axios.get(
          `${apiBaseUrl}/related_lists_bootstrap/${tableName}/${recordId}`,
          { headers: authHeaders(token) }
        );

        if (!isMounted) return;

        const parent = res.data?.parent || {};
        const related = (res.data?.related_lists || []).filter(
          (r) => !["created_by", "updated_by"].includes(r.field_name)
        );

        setParentTableLabel(parent.table_label || tableName);
        setDisplayValue(parent.display_value || "");
        setRelatedTables(related);

        if (related.length) {
          setSelectedTab(`${related[0].table_name}::${related[0].field_name}`);
        } else {
          setSelectedTab(null);
        }
      } catch (err) {
        console.error("❌ Failed to load related lists bootstrap:", err);
        if (!isMounted) return;
        setRelatedTables([]);
        setSelectedTab(null);
      }
    };

    loadRelatedBootstrap();

    return () => {
      isMounted = false;
    };
  }, [apiBaseUrl, tableName, recordId, token]);

  const parseTabKey = (key) => {
    const [table, field] = key.split("::");
    return { table, field };
  };

  const { table, field } = selectedTab ? parseTabKey(selectedTab) : {};

  const memoFilter = useMemo(() => {
    if (!table || !field || !recordId) return null;

    return {
      table,
      field,
      value: recordId,
      display_value: displayValue || "",
    };
  }, [table, field, recordId, displayValue]);

  const activeMeta = useMemo(() => {
    if (!selectedTab) return null;

    const meta = relatedTables.find(
      (rt) => `${rt.table_name}::${rt.field_name}` === selectedTab
    );

    if (!meta) return null;

    return {
      childTableName: meta.table_name,
      childTableLabel: meta.table_label || meta.table_name,
      childFieldLabel: meta.field_label || meta.field_name,
    };
  }, [selectedTab, relatedTables]);

  return (
    <div style={{ marginTop: 28 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <h3
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 800,
            color: "var(--ink, #0f1a1c)",
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Paperclip size={16} color="var(--brand, #0d7d6f)" /> Related Lists
        </h3>

        {!!displayValue && (
          <div style={{ fontSize: 12, color: "var(--muted, #5c6d6f)" }}>
            Parent: <strong>{parentTableLabel}</strong> —{" "}
            <span>{String(displayValue)}</span>
          </div>
        )}
      </div>

      <div className="rl-tabs" style={{ marginBottom: 10 }}>
        {relatedTables.length === 0 ? (
          <span style={{ color: "var(--muted, #5c6d6f)", fontSize: 13 }}>No related lists.</span>
        ) : (
          relatedTables.map((rt) => {
            const key = `${rt.table_name}::${rt.field_name}`;
            const isActive = key === selectedTab;

            return (
              <button
                key={key}
                onClick={() => setSelectedTab(key)}
                className={`rl-tab${isActive ? " active" : ""}`}
                title={`${rt.table_label || rt.table_name} • ${rt.field_label || rt.field_name}`}
              >
                {rt.table_label || rt.table_name}
              </button>
            );
          })
        )}
      </div>

      {selectedTab && memoFilter && activeMeta && (
        <div className="rl-wrap">
          <div className="rl-header">
            <h4 className="rl-title">{activeMeta.childTableLabel}</h4>

            <div className="rl-sub">
              via <strong>{activeMeta.childFieldLabel}</strong>
            </div>

            <div className="rl-actions">
              {(() => {
                const child = activeMeta.childTableName;
                const refField = memoFilter.field;
                const refValue = memoFilter.value;
                const refDisplay = encodeURIComponent(memoFilter.display_value || "");

                const newHref = `/dashboard/${child}/form_view/new?refField=${encodeURIComponent(
                  refField
                )}&refValue=${encodeURIComponent(refValue)}&refDisplay=${refDisplay}`;

                return (
                  <a href={newHref} className="rl-btn" title={`Create new ${activeMeta.childTableLabel}`}>
                    <span className="plus"><Plus size={14} /></span> New
                  </a>
                );
              })()}
            </div>
          </div>

          <div style={{ padding: 12 }}>
            <ListView
              referenceFilter={memoFilter}
              externalFilters={{ __tableName: activeMeta.childTableName }}
              hideHeader={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RelatedLists;